import '../../../app.scss';
import './nias-bar.scss';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'app/config/store';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import * as constants from './nias-bar-helper';
import { setLoaded } from 'app/shared/reducers/niasnavbar';
import { fetchAndLogoutKeycloak, initKeycloak, useKeycloak } from 'app/modules/keycloak/keycloak';
import { useAccessibility } from 'app/shared/accessibility/accessiblity.provider';
import { AccessibilityContrastEnum, AccessibilityDyslexiaEnum } from 'app/shared/accessibility/accessibility.enum';
import {LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS} from 'app/shared/util/local-storage-keys';
import {showToast} from "app/shared/util/toast.util";
import {toast} from "react-toastify";
import error = toast.error;
import {Col, Row} from "reactstrap";
import jwt from 'jwt-decode';
import { AUTH_TOKEN_KEY } from 'app/shared/reducers/authentication';
import { JWTUser } from 'app/shared/model/user.model';

const NiasNavbar = ({ isAuthenticated = false, hasAnonymousRole = false }) => {
  const { onSetContrast, onSetDyslexia, onIncreaseFontSize, onDecreaseFontSize, onResetFontSize } = useAccessibility();

  const dispatch = useDispatch();

  const authentication = useSelector((state: IRootState) => state.authentication);

  const niasNavbarLoaded = useSelector((state: IRootState) => state.niasNavbar.loaded);

  const [logButtonRendered, setLogButtonRendered] = useState(false);

  const isAuthenticatedRef = useRef(isAuthenticated);
  const hasAnonymousRoleRef = useRef(hasAnonymousRole);

  useEffect(() => {
    setTimeout(() => {
      const isKeycloakLocalLogout = localStorage.getItem(LOCAL_STORAGE_KEYS.KEYCLOAK_LOCAL_LOGOUT);

      if (isAuthenticated && niasNavbarLoaded && isKeycloakLocalLogout && isKeycloakLocalLogout === 'true') {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.KEYCLOAK_LOCAL_LOGOUT);
        showToast(error, 'Sesija je istekla ili ste odjavljeni prilikom jedinstvene odjave');
      }
    }, 100)
  }, [isAuthenticated, niasNavbarLoaded]);

  useEffect(() => {
    if (!niasNavbarLoaded) return;

    const intervalId = setInterval(() => {
      checkNiasNavbarStatus(niasNavbarLoaded, isAuthenticated, hasAnonymousRole);
    }, 1000);

    return () => clearInterval(intervalId);

  }, [niasNavbarLoaded, isAuthenticated, hasAnonymousRole]);

  const isImpersonatedUser = (): boolean => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    if (!token) {
      return false;
    }
    try {
      const user: JWTUser = jwt(token);
      return !!user.keycloakImpersonatorUsername;
    } catch (e) {
      return false;
    }
  };

  const checkNiasNavbarStatus = (niasNavbarLoaded, isAuthenticated, hasAnonymousRole) => {
    const isCustomLogin = localStorage.getItem('CUSTOM_LOGIN');
    const isImpersonate = isImpersonatedUser();

    if (!isCustomLogin && !isImpersonate && niasNavbarLoaded && isAuthenticated && !hasAnonymousRole) {
      const niasUserElement = document.getElementById("current_user");

      if (!niasUserElement || window.getComputedStyle(niasUserElement).display === "none") {
        axios.post('api/keycloak-local-logout').then(response => {
          localStorage.setItem(LOCAL_STORAGE_KEYS.KEYCLOAK_LOCAL_LOGOUT, 'true');
          fetchAndLogoutKeycloak();
        })
          .catch(error => {});
      }
    }
  };

  useEffect(() => {
    initKeycloak(dispatch).then(value => {
      const checkAuth = setInterval(() => {
        if (!authentication.loading) {
          clearInterval(checkAuth);

          if (!niasNavbarLoaded) {
            loadNiasNavBar();
          }
        }
      }, 100);

      const checkLogButton = setInterval(() => {
        if (document.getElementById(constants.NIAS_NAV_BAR_LOG_BUTTON)) {
          setLogButtonRendered(true);
          clearInterval(checkLogButton); // Stop checking once it's found
        }
      }, 100);

      return () => {
        clearInterval(checkLogButton);
        clearInterval(checkAuth);
      }; // Cleanup on unmount
    });
  }, []);

  useEffect(() => {
    if (logButtonRendered) {
      isAuthenticatedRef.current = isAuthenticated;
      hasAnonymousRoleRef.current = hasAnonymousRole;

      // u slucaju da je osoba ulogirana u navigacijskoj traci ali ne i aplikaciji, pokrenuti login automatski
      if (isAuthenticated && hasAnonymousRole) {
        const el = document.getElementsByClassName(constants.NIAS_NAV_BAR_LOGGED_IN_USER_INFO_PART)?.item(0);
        if (el && window.getComputedStyle(el).display === 'block') {
          axios.get('api/keycloak-properties').then(response => {
            if (response.data.autoRedirectToNias === true) {
              useKeycloak()?.login({ idpHint: 'saml-nias' });
            } else {
              useKeycloak()?.login();
            }
          });
        }
      }
    }
  }, [logButtonRendered, isAuthenticated]);

  const loadNiasNavBar = () => {
    axios.get(`/api/get-nias-nav-url`).then(response => {
      const script = document.createElement('script');

      script.async = true;
      script.src = response.data.niasNavbarUrl;

      script.onload = () => {
        setTimeout(() => {
          mapNiasHeaderButtonsToAccessibilityProvider();
        }, 100);

        setTimeout(() => {
          dispatch(setLoaded(true));
        }, 1000);
      };

      document.body.appendChild(script);
    });
  };

  const mapNiasHeaderButtonsToAccessibilityProvider = () => {
    document.getElementById(constants.NIAS_NAV_BAR_LOG_BUTTON).onclick = e => {
      e.preventDefault();
      handleLogin(e);
    };

    document.getElementById(constants.NIAS_NAV_BAR_ADD_DYSLEXIA_ID).onclick = e => {
      e.preventDefault();
      onSetDyslexia(AccessibilityDyslexiaEnum.DYSLEXIC);
    };

    document.getElementById(constants.NIAS_NAV_BAR_REMOVE_DYSLEXIA_ID).onclick = e => {
      e.preventDefault();
      onSetDyslexia(AccessibilityDyslexiaEnum.NON_DYSLEXIC);
    };

    document.getElementById(constants.NIAS_NAV_BAR_ADD_CONTRAST_ID).onclick = e => {
      e.preventDefault();
      onSetContrast(AccessibilityContrastEnum.IMPAIRED);
    };

    document.getElementById(constants.NIAS_NAV_BAR_REMOVE_CONTRAST_ID).onclick = e => {
      e.preventDefault();
      onSetContrast(AccessibilityContrastEnum.NON_IMPAIRED);
    };

    document.getElementById(constants.NIAS_NAV_BAR_SMALLER_FONT_ID).onclick = e => {
      e.preventDefault();
      onDecreaseFontSize();
    };

    document.getElementById(constants.NIAS_NAV_BAR_LARGER_FONT_ID).onclick = e => {
      e.preventDefault();
      onIncreaseFontSize();
    };

    document.getElementById(constants.NIAS_NAV_BAR_DEFAULT_FONT_ID).onclick = e => {
      e.preventDefault();
      onResetFontSize();
    };
  };

  const handleLogin = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticatedRef.current || hasAnonymousRoleRef.current) {
      axios.get('api/keycloak-properties').then(response => {
        if (response.data.autoRedirectToNias === true) {
          useKeycloak()?.login({ idpHint: 'saml-nias' });
        } else {
          useKeycloak()?.login();
        }
      });
    } else {
      fetchAndLogoutKeycloak();
    }
  };

  return (
    <>
      <div className="page-content" style={{zIndex: 1}}>
        <div className={`egradani_traka-holder ${isAuthenticated ? (hasAnonymousRole ? '' : 'authenticated') : ''}`}></div>
      </div>
    </>
  );
};

export default NiasNavbar;
